import React, { useState, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import ProductSD from './ProductSD';
import Price from './Price';
import Purchase from './Purchase';
import ExternalVideo from './ExternalVideo';
import viewAnalytics from './viewAnalytics';
import Top from '../Top/Top';
import TemplateParser from '../Templates/TemplateParser';
import LazyImage from '../../hocs/Lazy/LazyImage';
import Slider from '../../hocs/Slider/Slider';
import Breadcrumbs from '../Category/Breadcrumbs';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import { addRecentlyViewed } from '../../actions/checkout';
import { resetFilter } from '../../actions/environment';
import { purifier } from '../../utils/dom';
import { imageSizeTranslation } from '../../utils/image';
import { translate, prefix, translateObjectData } from '../../utils/locale';
import { splitNewLine, urlify, urlifyAllowAccents, createColors } from '../../utils/string';
import { origin } from '../../config';
import s from './Product.scss';

const Product = ({ data, currentPath, setup, productCache, filter, recentlyViewed, getParameters, ...props }) => {
	if (!data || !data.productData || !productCache?.[setup.market]?.[setup.pricelist]) return null;
	const product = translateObjectData(setup, data.productData);

	const { products: cache, productVariantLookup } = productCache[setup.market][setup.pricelist];
	const productId = product.id.slice(product.id.lastIndexOf('/') + 1);
	const productUri = urlifyAllowAccents(product.title);
	const pVariants = !Array.isArray(product.variants) ? Object.keys(product.variants).map((k) => product.variants[k]) : product.variants;
	const allVariants = pVariants.reduce((acc, v, i) => {
		let uri = v.selectedOptions ? Object.keys(v.selectedOptions).reduce((a, type) => (`${a}-${urlifyAllowAccents(v.selectedOptions[type])}`), '') : '';
		uri = uri.replace(/^-/, '');
		if (cache[`${productId}_${uri}`]) {
			acc[uri] = {
				id: v.id?.[setup.market] || '',
				cacheId: `${productId}_${uri}`,
				title: `${v.selectedOptions ? Object.keys(v.selectedOptions).map((type) => v.selectedOptions[type]).join(' ') : ''}`,
				uri,
				variantKey: i,
			};
		}
		return acc;
	}, {});

	let initialVariantKey = currentPath.slice(currentPath.lastIndexOf('/') + 1).replace(`${productUri}-`, '');
	if (getParameters.variant) {
		initialVariantKey = Object.keys(allVariants).filter((v) => allVariants[v].id.indexOf(getParameters.variant) !== -1)[0];
	}

	const [currentVariantKey, updateVariant] = useState(initialVariantKey);
	const [currentHoverVariantKey, updateHoverVariant] = useState('');
	const [clickedVariantKey, updateClickVariant] = useState(initialVariantKey);
	const [analytics, analyticsShown] = useState(false);
	// Expand/collapse product attributes.
	const [infoSection, setInfoSection] = useState('variant');

	useEffect(() => {
		if (analytics !== currentPath) {
			let updateVariantKey = currentPath.slice(currentPath.lastIndexOf('/') + 1).replace(`${productUri}-`, '');
			if (getParameters.variant) {
				updateVariantKey = Object.keys(allVariants).filter((v) => allVariants[v].id.indexOf(getParameters.variant) !== -1)[0];
			}
			if (updateVariantKey !== currentVariantKey) updateVariant(updateVariantKey);

			const tempCurrentVariant = allVariants[updateVariantKey];
			const tempCachedProduct = translateObjectData(setup, cache[tempCurrentVariant.cacheId]);
			if (availableForPurchase) props.addRecentlyViewed(tempCachedProduct.variantId);
			viewAnalytics(price, product, tempCachedProduct, setup, currentPath);
			analyticsShown(currentPath);
		}
	}, [currentPath, product, cachedProduct, price, setup, analytics]);

	if (!allVariants[currentVariantKey]) return true;

	const currentVariant = allVariants[currentVariantKey];
	const variant = pVariants[currentVariant.variantKey];

	const cachedProduct = translateObjectData(setup, cache[currentVariant.cacheId]);
	const availableForPurchase = Object.keys(cachedProduct).length > 0;
	const soldOut = cachedProduct.inStock === 0;
	const price = cachedProduct.price[setup.currency];
	const discount = !!price.compareAtPrice || (100 - Math.round((price.price / price.compareAtPrice) * 100));
	const sizes = (cachedProduct.sizes && cachedProduct.sizes.map((item) => ({ name: item.name, item: item.id, stock: item.availability }))) || [];

	const shareLocation = `${origin}${prefix(setup, cachedProduct.canonicalUri)}`;
	const productSections = data.parts || [];

	const metaInfo = {
		title: `${product.seo.title || product.title}`,
		metaDescription: product.seo.description || purifier(product.description, { ALLOWED_TAGS: ['#text'] }),
		linkCanonical: shareLocation,
	};

	const sortedImages = {
		general: [],
		variants: {},
	};
	const badges = [];
	let media = [];

	// eslint-disable-next-line consistent-return
	product.media.forEach((item) => {
		const imageAlt = `${urlifyAllowAccents(item.alt || '')}-${urlifyAllowAccents(item.src.slice(item.src.lastIndexOf('/') + 1))}`;
		let generalMedia = true;
		Object.keys(allVariants).forEach((variantUri) => {
			if (imageAlt.indexOf(variantUri) !== -1) {
				generalMedia = false;
				if (!sortedImages.variants[variantUri]) sortedImages.variants[variantUri] = [];
				sortedImages.variants[variantUri].push(item);
			}
		});
		if (generalMedia) return sortedImages.general.push(item);
	});

	media = sortedImages.variants[currentVariant.uri] || sortedImages.general || [];

	if (!media || media.length === 0) media = product.media;

	if (!soldOut && product.tags && product.tags.indexOf('new') !== -1) {
		badges.unshift(<span key="new-badge" className={[s.productDetail_badge, s.productDetail_badge__new].join(' ')}>{ translate(setup, 'new') }</span>);
	} else if (!soldOut && !!price.compareAtPrice && price.compareAtPrice !== price.price) {
		badges.unshift(<span key="sale-badge" className={[s.productDetail_badge, s.productDetail_badge__sale].join(' ')}>-{ discount }%</span>);
	}

	const toggleInfoSection = (e) => {
		e.preventDefault();
		const section = e.currentTarget.getAttribute('href').replace('#toggle-', '');
		setInfoSection((prevState) => (prevState === section ? null : section));
	};

	const mouseEnterProductPreview = (e) => {
		if (window.innerWidth > 900) {
			const id = e.currentTarget.getAttribute('id');
			updateVariant(id);
			updateHoverVariant(id);
		}
	};

	const mouseLeaveProductPreview = () => {
		if (window.innerWidth > 900) {
			updateVariant(clickedVariantKey);
			updateHoverVariant('');
		}
	};

	const clickProductPreview = (e) => {
		const id = e.currentTarget.getAttribute('id');
		updateClickVariant(id);
		updateHoverVariant('');
		setInfoSection(0);
	};

	const variants = [...Object.keys(allVariants).map((item) => (
		{ colors: createColors(pVariants?.[allVariants[item].variantKey]?.metafields?.color), title: allVariants[item].title, uri: `${productUri}-${item}`, id: item, selected: clickedVariantKey === item }
	))].sort((a, b) => {
		const [, aOrder = 'z'] = a.colors[0] || [[]];
		const [, bOrder = 'z'] = b.colors[0] || [[]];
		return aOrder.localeCompare(bOrder);
	});

	const noColors = variants.filter((v) => !v.colors?.length).length === variants.length;
	const isGiftCard = product.productType && urlify(product.productType).indexOf('gift-card') !== -1;

	return (
		<main role="main">
			<Top setup={setup} pageContent={metaInfo} paths={data.paths} skipGeneral />
			<ProductSD metaInfo={metaInfo} product={product} soldOut={soldOut} price={price} currency={setup.countries[setup.country].currency} image={media.map((i) => i.src)} />
			<div id="productTop" className={s.product}>
				<ErrorBoundary message="Product images missing data" debugData={product}>
					<div className={s.productImages}>
						{
							media.length > 0 && Object.keys(allVariants).length > 1 && (
								<div className={s.productImages_quickHovers}>
									{
										Object.keys(allVariants).map((v) => {
											if (!sortedImages?.variants?.[v]) return null;
											const firstImage = sortedImages.variants[v].filter((sI) => sI.type === 'IMAGE')[0];

											return (
												<div className={[s.productImages_quickHover, (currentHoverVariantKey === v ? s.productImages_quickHover__selected : null)].join(' ')} key={firstImage.src.slice(-40)}>
													<img {...imageSizeTranslation('7', firstImage, '_', false)} />
												</div>
											);
										})
									}
								</div>
							)
						}
						{
							media.length > 0 && (
								<Slider adaption="product" infinite>
									{
										media.map((item) => (
											<figure key={item.src} className={s.productImages_media}>
												<div className={s.productImages_media_image}>
													{ item.type === 'IMAGE' && <LazyImage {...imageSizeTranslation('7', item, '_')} /> }
													{ item.type === 'EXTERNAL_VIDEO' && <ExternalVideo url={item.src} uid={urlifyAllowAccents(item.alt)} /> }
												</div>
											</figure>
										))
									}
								</Slider>
							)
						}
					</div>
				</ErrorBoundary>
				<div className={s.productDetail}>

					<ErrorBoundary message="Product details missing data" debugData={product}>
						<Breadcrumbs currentPath={currentPath} filter={filter} filtering={productCache[setup.market][setup.pricelist]} data={data} setup={setup} product={cachedProduct.variant} resetFilter={props.resetFilter} />
						<div className={s.productDetail_top}>
							<span className={s.productDetail_top_price}>
								<span className={s.productDetail_top_price_typeWriter}>(</span>
								<Price price={price} setup={setup} soldOut={soldOut} product />
								<span className={s.productDetail_top_price_typeWriter}>)</span>
							</span>
							<h1 className={s.productDetail_top_title}>
								{product.title}<br />
								{cachedProduct.variant}<br />
								{cachedProduct.weight}
							</h1>
						</div>
						<div className={[s.productDetail_purchase, (isGiftCard ? s.productDetail_purchase__giftCard : null)].join(' ')}>
							<Purchase setup={setup} cache={cache} productVariantLookup={productVariantLookup} currentPath={currentPath} cachedProduct={cachedProduct} productId={data.productData.product} originalProduct={data.productData} sizes={sizes} price={price} soldOut={soldOut ? translate(setup, 'soldOut') : false} />
							{
								variants && variants.length > 1 && (
									<ErrorBoundary message="Related Product details missing data" debugData={variants}>
										<div className={[s.productDetail_variants, (noColors ? s.productDetail_variants__noColors : null)].join(' ')}>
											{
												variants.map((item) => {
													const VariantTag = item.selected ? 'span' : Link;
													const attrs = {};
													if (!item.selected) {
														attrs.to = prefix(setup, `${currentPath.slice(0, currentPath.lastIndexOf('/'))}/${item.uri}`);
														attrs.onMouseEnter = mouseEnterProductPreview;
														attrs.onMouseLeave = mouseLeaveProductPreview;
														attrs.onClick = clickProductPreview;
													}
													return (
														<VariantTag id={item.id} key={item.id} className={[s.productDetail_variant, (item?.colors?.length ? null : s.productDetail_variant__noColor), (item.selected ? s.productDetail_variant__selected : null)].join(' ')} {...attrs}>
															{
																item?.colors?.length ? item.colors.map(([hex = '#000']) => (
																	<span key={hex} className={s.productDetail_variant_swatch} style={{ backgroundColor: hex }} />
																)) : null
															}
															{
																!item?.colors?.length && <span className={s.productDetail_variant_title}>{ item.title }</span>
															}
														</VariantTag>
													);
												})
											}
										</div>
									</ErrorBoundary>
								)
							}
						</div>

						{
							product?.metafields?.keyWords && (
								<div className={s.productDetail_keyWords}>
									<ul>
										{
											splitNewLine(product.metafields.keyWords).filter((kW) => !!kW).map((word) => <li key={urlify(word.slice(0, 20))}>{ word }</li>)
										}
									</ul>
								</div>
							)
						}

						<div className={[s.productDetail_infoSections, (!variants || variants.length === 1 ? s.productDetail_infoSections__noVariants : null)].join(' ')}>
							{
								variant?.metafields?.description && (
									<div className={[s.productDetail_infoSection, (infoSection === 'variant' ? null : s.productDetail_infoSection__collapsed)].join(' ')}>
										<a className={s.productDetail_infoSection_link} href="#toggle-variant" onClick={toggleInfoSection}>
											<h4 className={s.productDetail_infoSection_title}>{ translate(setup, 'description') }</h4>
											<svg width="6" height="4" viewBox="0 0 6 4" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 0L5.59808 3.75L0.401924 3.75L3 0Z" fill="black" /></svg>
										</a>
										<p className={s.productDetail_infoSection_content}>{ variant.metafields.description }</p>
									</div>
								)
							}
							{
								variant?.metafields?.howToUse && (
									<div className={[s.productDetail_infoSection, (infoSection === 'how-to-use' ? null : s.productDetail_infoSection__collapsed)].join(' ')}>
										<a className={s.productDetail_infoSection_link} href="#toggle-how-to-use" onClick={toggleInfoSection}>
											<h4 className={s.productDetail_infoSection_title}>{ translate(setup, 'howToUse') }</h4>
											<svg width="6" height="4" viewBox="0 0 6 4" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 0L5.59808 3.75L0.401924 3.75L3 0Z" fill="black" /></svg>
										</a>
										<ul className={s.productDetail_infoSection_content}>
											{
												splitNewLine(variant.metafields.howToUse).map((v) => <li key={urlify(v.slice(0, 20))}>{ v }</li>)
											}
										</ul>
									</div>
								)
							}
							{
								variant?.metafields?.ingredients && (
									<div className={[s.productDetail_infoSection, (infoSection === 'ingredients' ? null : s.productDetail_infoSection__collapsed)].join(' ')}>
										<a className={s.productDetail_infoSection_link} href="#toggle-ingredients" onClick={toggleInfoSection}>
											<h4 className={s.productDetail_infoSection_title}>{ translate(setup, 'ingredients') }</h4>
											<svg width="6" height="4" viewBox="0 0 6 4" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 0L5.59808 3.75L0.401924 3.75L3 0Z" fill="black" /></svg>
										</a>
										<p className={s.productDetail_infoSection_content}>{ variant.metafields.ingredients }</p>
									</div>
								)
							}
							{
								product.descriptionHtml && (
									<div className={[s.productDetail_infoSection, (infoSection === 'description' ? null : s.productDetail_infoSection__collapsed)].join(' ')}>
										<a className={s.productDetail_infoSection_link} href="#toggle-description" onClick={toggleInfoSection}>
											<h4 className={s.productDetail_infoSection_title}>{ translate(setup, 'productDescription') }</h4>
											<svg width="6" height="4" viewBox="0 0 6 4" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 0L5.59808 3.75L0.401924 3.75L3 0Z" fill="black" /></svg>
										</a>
										<div className={[s.productDetail_infoSection_content, s.productDetail_infoSection_content__raw].join(' ')} dangerouslySetInnerHTML={{ __html: purifier(product.descriptionHtml) }} />
									</div>
								)
							}
						</div>
					</ErrorBoundary>
				</div>
			</div>
			{
				productSections.length > 0 && (
					<div className={s.productCMS}>
						{
							productSections.map((section) => (
								<TemplateParser sections={section.sections} setup={setup} isProduct />
							))
						}
					</div>
				)
			}
		</main>
	);
};

const mapState = ({ environment, productCache, checkout, filter }, { data, currentPath }) => ({
	data,
	currentPath,
	getParameters: environment.getParameters,
	header: environment.header,
	setup: environment.setup,
	productCache,
	recentlyViewed: checkout.recentlyViewed,
	filter,
});

export default hot(module)(connect(mapState, { resetFilter, addRecentlyViewed })(Product));
